<template>
  <div class="datetime-picker-field">
    <div class="flex justify-content-between flex-wrap">
      <div class="flex justify-content-center">
        <label
          class="block my-3"
          data-test-id="datetime-picker-label"
        >
          {{ t(configs.label, null) }}
        </label>
      </div>
      <div class="flex justify-content-center align-items-center">
        <ToggleDisplaySettings
          v-if="!Object.prototype.hasOwnProperty.call(configs, 'displayToggle') || configs.displayToggle"
          :configs="configs"
          :parser-values="parserValues"
          :element-part-type="elementPartType"
          @display-settings="(value) => displayProperties = value"
        />
      </div>
    </div>
    <Calendar
      v-model="selectedDate"
      :class="{ 'hidden': !displayProperties }"
      class="w-full"
      :show-time="true"
      :show-seconds="false"
      hour-format="24"
      date-format="dd/mm/yy"
      :min-date="minDate"
      :show-icon="true"
      :show-button-bar="true"
      @update:model-value="handleDateChange"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, Ref, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Calendar from 'primevue/calendar';
import { FieldConfig, ParserFieldObject, Property } from '@/types';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';

export default defineComponent({
  name: 'DatetimePicker',

  components: {
    ToggleDisplaySettings,
    Calendar,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['on-change-properties'],

  setup(props, context) {
    const { t } = useI18n();
    const configs = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));
    const property: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties[0]);

    // Parse initial date from ISO string
    const parseDate = (value: string): Date => {
      if (!value) {
        // Default: 7 days from now
        return new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      }
      const date = new Date(value);
      return isNaN(date.getTime()) ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : date;
    };

    const selectedDate = ref<Date>(parseDate(property.value?.value ?? props.configs.options?.defaultValue));

    // Minimum date is now
    const minDate = computed(() => new Date());

    const handleDateChange = (value: Date) => {
      if (!value) return;

      selectedDate.value = value;
      const isoString = value.toISOString();

      if (property.value) {
        property.value.value = isoString;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };

    return {
      t,
      configs,
      displayProperties,
      selectedDate,
      minDate,
      handleDateChange,
    };
  },
});
</script>

<style scoped lang="scss">
.datetime-picker-field {
  :deep(.p-calendar) {
    width: 100%;

    .p-inputtext {
      width: 100%;
    }
  }
}
</style>
